import React, { ReactNode } from "react";
import { theme } from "src/config";
import styled, { css } from "styled-components";
import { Row, Text } from "..";
import { Chevron } from "./chevron";


export interface IRoomSidebarRowProps {
  row: Row,
}

export const RoomSidebarRow = (props: IRoomSidebarRowProps) => {
  const { row } = props;
  const [showChildren, setShowChildren] = React.useState<boolean>(false);

  const action   = React.useMemo(() => 
    row.action || (row.children?.length ? <Chevron up={showChildren} /> : undefined)
  , [row.action, row.children, showChildren]);
  const onAction = React.useCallback( 
    row.onAction || (row.children?.length ? () => setShowChildren(!showChildren) : undefined)
  , [row.onAction, row.children, showChildren]);

  return (
    <>
      <ItemContainer
        active    = {row.active}
        divider   = {row.divider}
        hide      = {row.hide}
        isSubItem = {row.subItemDepth}
        key       = {row.key || row.label}
        hasLabel  = {Boolean(row.label)}
        onClick   = {!row.disabled ? row.onClick : undefined}
        style     = {row.style}
      >
        <LabelContainer truncated show={true} size="small">
          {row.label}
        </LabelContainer>
        {action && onAction && 
          <ActionContainer
            onClick={(e) => { e.stopPropagation(); onAction(); }}
          >
            {action}
          </ActionContainer>
        }
      </ItemContainer>
      {row.children && showChildren && 
        row.children.map(c => (
          <RoomSidebarRow 
            key = {c.key}
            row = {{...c, subItemDepth: (row.subItemDepth ? row.subItemDepth+1 : 1)}}
          />
        ))
      }
    </>
  );
}

const LabelContainer = styled(Text) <{ onClick?: () => void }>`
  color:           ${(props) => (props.show ? "white" : "rgba(0, 0, 0, 0)")};
  justify-content: left;
  display:         inline-block;
  width:           100%;
  align-items:     center;
  font-weight:     ${theme.font.weight.normal};
  font-size:       ${theme.font.size.default};
`;

const ActionContainer = styled.div`
  width:           1.4rem;
  min-width:       1.4rem;
  padding:         0.1rem;
  display:         flex;
  justify-content: center;
  border-radius:   50%;
`;

const ItemContainer = styled.li<{
  active:     boolean;
  divider?:   boolean;
  hide:       boolean;
  isSubItem?: number;
  hasLabel?:  boolean;
}>`
  display:        flex;
  flex-direction: row;
  color:          ${theme.color.white};
  align-items:    center;
  text-overflow:  ellipsis;
  white-space:    nowrap;
  overflow:       hidden;
  
  ${(props) =>
    props.hide
      ? css`
          padding: 0rem 0.5rem 0rem 0rem;
        `
      : css`
          padding: 0.75rem 0.5rem 0.75rem 2.5rem;
        `
  };
  ${props => 
    props.isSubItem && css`
      padding-left: ${props.isSubItem+2.5}rem;
    `
  };
  height:   ${(props) => ((props.hide || !props.hasLabel) ? "0" : "3.2rem")};
  opacity:  ${(props) => (props.hide ? "0" : "1")};
  overflow: hidden;
  :hover {
    background-color: ${theme.color.sidebar.selected};
    cursor:           pointer;
  }
  ${(props) =>
    props.active
      ? css`
          background-color: ${theme.color.sidebar.selected};
          color:            ${theme.color.sidebar.icon};
        `
      : css`
          background-color: black;
        `};

  ${(props) =>
    props.active &&
    css`
      transform:     translateX(0%);
      transition:    margin-left 0.3s ease-in-out;
      border-bottom: none;
    `}

  ${(props) =>
    props.divider &&
    css`
      border-bottom: 1px solid #696969;
    `}
`;